import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import type {
  BoothMediaUpload,
  BrandingAsset,
  DeveloperBooth as DeveloperBoothRead,
  DeveloperBoothTemplate,
  Hold,
  HoldPolicy,
  Organization,
  Project,
  QueueEntryRow,
} from '../../lib/api/types';
import type { StringKey } from '../../lib/i18n/strings';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select, TextArea } from '../../ui/primitives';
import { DegradedState, EmptyState, ErrorState, LoadingState, SuccessNote } from '../../ui/states';
import { AuditStatement, RefusalWithRemedy } from '../../ui/guards';
import { ReadinessPanel } from '../../ui/readiness';
import { MediaPreview, developerMediaPreviewUrl } from '../../ui/media';
import { statusTone } from '../admin/Expos';

/**
 * The Developer Portal is resolved through a PARTICIPATION, never through the organization alone.
 * Every screen below therefore asks for the participation id first and says so.
 */
function ParticipationPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}): ReactElement {
  const { t } = useI18n();
  return (
    <Field
      label="participation_id"
      name="participation_id"
      value={value}
      onChange={onChange}
      ltr
      hint={t('dev.booth.desc')}
    />
  );
}

/** `DEV-11` booth configuration · `DEV-12` branding. */
export function DeveloperBooth(): ReactElement {
  const { t } = useI18n();
  const [participationId, setParticipationId] = useState('');
  const path = participationId ? `/v1/developer/booths/${participationId}` : null;
  /**
   * THE ENTITLEMENT LIMITS COME FROM THE DEVELOPER'S OWN READ.
   *
   * They used to be fetched from `/v1/admin/booth-templates` — an ADMIN route called from the
   * Developer Portal, refused for every real exhibitor, with the refusal rendered nowhere; the
   * screen then drew no limits and looked identical to a booth that has none. The route was
   * disabled and the screen said so instead, which was honest and still useless. The booth read now
   * carries `template` and `readiness`, so the exhibitor sees their own entitlements and their own
   * blockers from a route they are allowed to call.
   */
  const booth = useApiResource<DeveloperBoothRead>(path);
  const patch = useApiAction<DeveloperBoothRead>([path]);
  const [seatCount, setSeatCount] = useState('');
  const [accent, setAccent] = useState('');

  const version = booth.data?.version ?? booth.data?.data.version ?? null;
  const template = booth.data?.data.template ?? null;
  const readiness = booth.data?.data.readiness ?? null;

  return (
    <>
      <PageTitle title={t('dev.booth.title')}>{t('dev.booth.desc')}</PageTitle>
      <Card title={t('dev.booth.title')}>
        <ParticipationPicker value={participationId} onChange={setParticipationId} />
        {booth.isLoading ? <LoadingState /> : null}
        {booth.error ? <ErrorState error={booth.error} onRetry={() => void booth.refetch()} /> : null}
        {booth.data ? (
          <>
            <dl className="kv">
              <dt>{t('table.status')}</dt>
              <dd>
                <Badge tone={statusTone(booth.data.data.status)}>{booth.data.data.status}</Badge>
              </dd>
              <dt>publication_state</dt>
              <dd>{booth.data.data.publication_state}</dd>
              <dt>stage</dt>
              <dd>{booth.data.data.stage}</dd>
              <dt>{t('admin.participations.slot')}</dt>
              <dd>{booth.data.data.booth_slot_code ? <Ltr>{booth.data.data.booth_slot_code}</Ltr> : t('table.none')}</dd>
              <dt>seat_count</dt>
              <dd className="numeral">{booth.data.data.seat_count}</dd>
              <dt>virtual_seats</dt>
              <dd className="numeral">{booth.data.data.virtual_seats}</dd>
              <dt>{t('admin.expos.version')}</dt>
              <dd className="numeral">{version}</dd>
            </dl>
            {/* Entitlement limits are shown inline (DEV-11). */}
            {template ? (
              <p className="muted">
                <Ltr>
                  {template.display_name} ({template.code}) · {template.width_m}×{template.depth_m}×
                  {template.height_m} m · max_seats {template.max_seats} · max_branding_slots{' '}
                  {template.max_branding_slots}
                </Ltr>
              </p>
            ) : (
              <DegradedState title={t('dev.booth.noTemplate')}>
                <p>{t('dev.booth.noTemplateHint')}</p>
              </DegradedState>
            )}
            <Field label="seat_count" name="seat_count" type="number" value={seatCount} onChange={setSeatCount} ltr />
            <Field label="config.accent_color" name="accent" value={accent} onChange={setAccent} ltr />
            {patch.error ? (
              <RefusalWithRemedy
                error={patch.error}
                action="booth.manage"
                retryExact={patch.retryLast}
                onReread={() => void booth.refetch()}
              />
            ) : null}
            <button
              className="btn"
              type="button"
              disabled={patch.isPending || version === null || !path}
              onClick={() =>
                patch.mutate(newIntent({
                  path: path as string,
                  method: 'PATCH',
                  body: {
                    ...(seatCount ? { seat_count: Number(seatCount) } : {}),
                    ...(accent ? { config: { accent_color: accent } } : {}),
                  },
                  ifMatch: version as number,
                }))
              }
            >
              {t('action.save')}
            </button>
          </>
        ) : null}
      </Card>

      {readiness ? (
        <Card title={t('readiness.title')} description={t('dev.readiness.desc')} testId="booth-readiness">
          {/* READ-ONLY HERE. An exhibitor cannot publish their own booth; what this panel is for is
              telling them which of the things THEY control is still missing. */}
          <ReadinessPanel readiness={readiness} />
        </Card>
      ) : null}

      {participationId ? <DeveloperBranding participationId={participationId} template={template} /> : null}
      {participationId ? <DeveloperAnnounce participationId={participationId} /> : null}
    </>
  );
}

/**
 * THE THREE THINGS AN EXHIBITOR PUTS ON THEIR BOOTH.
 *
 * The world reads exactly these kinds; a logo is required before a booth may be published, the
 * other two are optional. They are named here rather than left as a free-text `kind` box, because
 * a value the manifest does not know about is an upload that will never appear anywhere.
 */
const BRANDING_KINDS: { kind: string; label: StringKey; required: boolean; previewable: boolean }[] = [
  { kind: 'logo', label: 'dev.branding.kind.logo', required: true, previewable: true },
  { kind: 'hero_banner', label: 'dev.branding.kind.hero_banner', required: false, previewable: true },
  { kind: 'screen_video', label: 'dev.branding.kind.screen_video', required: false, previewable: false },
];

function DeveloperBranding({
  participationId,
  template,
}: {
  participationId: string;
  template: DeveloperBoothTemplate | null;
}): ReactElement {
  const { t } = useI18n();
  const path = `/v1/developer/booths/${participationId}/branding`;
  const assets = useApiList<BrandingAsset>(path);

  const items = assets.data?.items ?? [];
  const used = items.length;
  const limit = template?.max_branding_slots ?? null;

  return (
    <Card title={t('dev.branding.title')} description={t('dev.branding.desc')}>
      {limit === null ? null : (
        <p className="muted">{t('dev.branding.quota', { used, limit })}</p>
      )}
      {assets.isLoading ? <LoadingState /> : null}
      {assets.error ? <ErrorState error={assets.error} onRetry={() => void assets.refetch()} /> : null}
      {assets.data
        ? BRANDING_KINDS.map((slot) => (
            <BrandingSlot
              key={slot.kind}
              participationId={participationId}
              kind={slot.kind}
              label={t(slot.label)}
              required={slot.required}
              previewable={slot.previewable}
              assets={items.filter((a) => a.kind === slot.kind)}
              atQuota={limit !== null && used >= limit}
            />
          ))
        : null}
    </Card>
  );
}

/**
 * ONE STEP, NOT TWO.
 *
 * Uploading used to hand back a `media_asset_id` that the exhibitor then RETYPED into a second
 * field and submitted separately — two requests, one of them a copy-paste of an opaque uuid, and a
 * half-finished state (bytes stored, nothing bound to the booth) whenever they stopped in between.
 * `POST .../media` now takes the `kind` and the `locale` with the file and creates the binding in
 * the same request, so there is nothing to carry and no intermediate state to leave behind.
 */
function BrandingSlot({
  participationId,
  kind,
  label,
  required,
  previewable,
  assets,
  atQuota,
}: {
  participationId: string;
  kind: string;
  label: string;
  required: boolean;
  previewable: boolean;
  assets: BrandingAsset[];
  atQuota: boolean;
}): ReactElement {
  const { t } = useI18n();
  const brandingPath = `/v1/developer/booths/${participationId}/branding`;
  const boothPath = `/v1/developer/booths/${participationId}`;
  const upload = useApiAction<BoothMediaUpload>([brandingPath, boothPath]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [locale, setLocale] = useState('ar');
  const [noFile, setNoFile] = useState(false);
  const inputId = `branding-file-${kind}`;

  return (
    <section className="branding-slot" aria-label={label}>
      <div className="branding-slot__heading">
        <h3>{label}</h3>
        <Badge tone={required ? 'warn' : 'neutral'}>
          {required ? t('readiness.required') : t('readiness.optional')}
        </Badge>
      </div>

      {assets.length === 0 ? (
        <EmptyState title={t('dev.branding.none')} />
      ) : (
        <ul className="branding-slot__assets">
          {assets.map((a) => (
            <li key={a.asset_id}>
              {previewable ? (
                <MediaPreview
                  src={developerMediaPreviewUrl(participationId, a.media_asset_id)}
                  alt={t('dev.branding.previewAlt', { kind: label })}
                />
              ) : (
                <p className="muted">{t('dev.branding.noStill')}</p>
              )}
              <p className="row">
                <Badge tone="neutral"><Ltr>{a.locale}</Ltr></Badge>
                <Badge tone={statusTone(a.status)}>{a.status}</Badge>
              </p>
              {a.reason_code ? (
                <p className="muted">
                  {t('readiness.reason')}: <Ltr>{a.reason_code}</Ltr>
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <Select
        label={t('dev.branding.locale')}
        name={`branding-locale-${kind}`}
        value={locale}
        options={[
          { value: 'ar', label: 'ar' },
          { value: 'en', label: 'en' },
        ]}
        onChange={setLocale}
      />
      <div className="field">
        <label htmlFor={inputId}>{t('dev.branding.file')}</label>
        <input id={inputId} ref={fileRef} type="file" name="file" />
        {noFile ? <span className="field-error">{t('dev.branding.noFile')}</span> : null}
      </div>
      {upload.error ? <ErrorState error={upload.error} /> : null}
      {upload.data ? <SuccessNote>{t('dev.branding.uploaded')}</SuccessNote> : null}
      <button
        className="btn"
        type="button"
        disabled={upload.isPending || atQuota}
        onClick={() => {
          const file = fileRef.current?.files?.[0];
          if (!file) {
            setNoFile(true);
            return;
          }
          setNoFile(false);
          const form = new FormData();
          form.set('file', file);
          form.set('kind', kind);
          form.set('locale', locale);
          upload.mutate(newIntent({
            path: `/v1/developer/booths/${participationId}/media`,
            formData: form,
            label: `upload ${kind}`,
          }));
        }}
      >
        {t('action.upload')}
      </button>
      {atQuota ? <p className="muted">{t('dev.branding.atQuota')}</p> : null}
    </section>
  );
}

/**
 * `DEV-02` — the organization's own profile.
 *
 * An organization had no editable identity at all: the legal names and the verification state are
 * the platform's to decide, but the website and the two "about" texts are the exhibitor's own words
 * and there was nowhere to write them. This is a compare-and-swap like every other tenancy write —
 * the version read here is sent as `If-Match`, so two people editing the same profile is a conflict
 * rather than a silent overwrite.
 */
export function DeveloperCompany(): ReactElement {
  const { t } = useI18n();
  const path = '/v1/developer/organization';
  const org = useApiResource<Organization>(path);
  const save = useApiAction<Organization>([path]);

  const [website, setWebsite] = useState('');
  const [aboutAr, setAboutAr] = useState('');
  const [aboutEn, setAboutEn] = useState('');

  useEffect(() => {
    const current = org.data?.data;
    if (!current) return;
    setWebsite(current.website_url);
    setAboutAr(current.about_ar);
    setAboutEn(current.about_en);
  }, [org.data]);

  const current = org.data?.data;
  const version = org.data?.version ?? current?.version ?? null;
  // The route's own rules, stated before the attempt rather than discovered as a 422.
  const websiteError = website !== '' && !/^https:\/\/\S+$/i.test(website) ? t('dev.company.websiteError') : undefined;
  const aboutArError = aboutAr.length > 2000 ? t('dev.company.aboutError') : undefined;
  const aboutEnError = aboutEn.length > 2000 ? t('dev.company.aboutError') : undefined;
  const invalid = Boolean(websiteError ?? aboutArError ?? aboutEnError);

  return (
    <>
      <PageTitle title={t('dev.company.title')}>{t('dev.company.desc')}</PageTitle>

      <Card title={t('dev.company.identity')} description={t('dev.company.identityHint')}>
        {org.isLoading ? <LoadingState /> : null}
        {org.error ? <ErrorState error={org.error} onRetry={() => void org.refetch()} /> : null}
        {current ? (
          <dl className="kv">
            <dt>{t('dev.company.legalNameAr')}</dt>
            <dd>{current.legal_name_ar}</dd>
            <dt>{t('dev.company.legalNameEn')}</dt>
            <dd>
              <Ltr>{current.legal_name_en}</Ltr>
            </dd>
            <dt>{t('dev.company.displayName')}</dt>
            <dd>{current.display_name}</dd>
            <dt>{t('table.status')}</dt>
            <dd>
              <Badge tone={statusTone(current.status)}>{current.status}</Badge>
            </dd>
            <dt>{t('dev.company.verification')}</dt>
            <dd>
              <Badge tone={statusTone(current.verification_state)}>
                <Ltr>{current.verification_state}</Ltr>
              </Badge>
            </dd>
            <dt>{t('dev.company.country')}</dt>
            <dd>
              <Ltr>{current.country}</Ltr>
            </dd>
            <dt>{t('admin.expos.version')}</dt>
            <dd className="numeral">{version}</dd>
          </dl>
        ) : null}
      </Card>

      {current ? (
        <Card title={t('dev.company.profile')} description={t('dev.company.profileHint')}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (version === null || invalid) return;
              save.mutate(newIntent({
                path,
                method: 'PATCH',
                ifMatch: version,
                label: 'update organization profile',
                body: { website_url: website, about_ar: aboutAr, about_en: aboutEn },
              }));
            }}
          >
            <Field
              label={t('dev.company.website')}
              hint={t('dev.company.websiteHint')}
              name="website_url"
              type="url"
              value={website}
              onChange={setWebsite}
              ltr
              error={websiteError}
            />
            <TextArea
              label={t('dev.company.aboutAr')}
              name="about_ar"
              value={aboutAr}
              onChange={setAboutAr}
              error={aboutArError}
            />
            <TextArea
              label={t('dev.company.aboutEn')}
              name="about_en"
              value={aboutEn}
              onChange={setAboutEn}
              ltr
              error={aboutEnError}
            />
            {save.error ? (
              <RefusalWithRemedy
                error={save.error}
                action="organization.manage"
                retryExact={save.retryLast}
                onReread={() => void org.refetch()}
              />
            ) : null}
            {save.data ? <SuccessNote>{t('dev.company.saved')}</SuccessNote> : null}
            <button className="btn" type="submit" disabled={save.isPending || version === null || invalid}>
              {t('action.save')}
            </button>
          </form>
        </Card>
      ) : null}
    </>
  );
}

function DeveloperAnnounce({ participationId }: { participationId: string }): ReactElement {
  const { t } = useI18n();
  const announce = useApiAction([`/v1/developer/booths/${participationId}`]);
  const [ids, setIds] = useState('');

  return (
    <Card title={t('dev.announce.title')} description={t('dev.announce.desc')}>
      <AuditStatement>
        <p>
          <Ltr>participation.update</Ltr> — announcing drives <Ltr>PROJECTS_ANNOUNCED</Ltr>.
        </p>
      </AuditStatement>
      <Field label="project_ids" name="project_ids" value={ids} onChange={setIds} ltr />
      {announce.error ? <ErrorState error={announce.error} /> : null}
      <button
        className="btn"
        type="button"
        disabled={announce.isPending || ids.trim() === ''}
        onClick={() =>
          announce.mutate(newIntent({
            path: `/v1/developer/booths/${participationId}/projects:announce`,
            body: {
              project_ids: ids
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            },
          }))
        }
      >
        {t('dev.announce.title')}
      </button>
    </Card>
  );
}

/** `DEV-03`…`DEV-08` — the catalogue self-service. */
export function DeveloperCatalogue(): ReactElement {
  const { t, bilingual } = useI18n();
  const createProject = useApiAction<Project>([]);
  const projectAction = useApiAction<Project>([]);
  const createUnitType = useApiAction([]);
  const importUnits = useApiAction<{ accepted?: number; rejected?: { row: number; rule: string }[] }>([]);
  const setPrice = useApiAction([]);
  const createOffer = useApiAction([]);

  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [projectId, setProjectId] = useState('');
  const project = useApiResource<Project>(projectId ? `/v1/projects/${projectId}` : null);

  const [unitTypeCode, setUnitTypeCode] = useState('');
  const [importRows, setImportRows] = useState('');
  const [unitId, setUnitId] = useState('');
  const [priceMinor, setPriceMinor] = useState('');
  const [priceVersion, setPriceVersion] = useState('');
  const [currency, setCurrency] = useState('SAR');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [offerScope, setOfferScope] = useState('project');
  const [offerScopeId, setOfferScopeId] = useState('');
  const [offerFrom, setOfferFrom] = useState('');
  const [offerTo, setOfferTo] = useState('');
  const [offerTitleAr, setOfferTitleAr] = useState('');
  const [offerTitleEn, setOfferTitleEn] = useState('');

  return (
    <>
      <PageTitle title={t('dev.projects.title')}>{t('dev.projects.desc')}</PageTitle>

      <Card title={t('dev.projects.title')}>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            createProject.mutate(
              newIntent({ path: '/v1/developer/projects', body: { name_ar: nameAr, name_en: nameEn } }),
              { onSuccess: (p) => setProjectId(p.project_id) },
            );
          }}
        >
          <Field label="name_ar" name="project-ar" value={nameAr} onChange={setNameAr} required />
          <Field label="name_en" name="project-en" value={nameEn} onChange={setNameEn} required ltr />
          {createProject.error ? <ErrorState error={createProject.error} /> : null}
          <button className="btn" type="submit" disabled={createProject.isPending}>
            {t('action.create')}
          </button>
        </form>

        <Field label="project_id" name="project_id" value={projectId} onChange={setProjectId} ltr />
        {project.isLoading ? <LoadingState /> : null}
        {project.error ? <ErrorState error={project.error} /> : null}
        {project.data ? (
          <>
            <dl className="kv">
              <dt>{t('table.name')}</dt>
              <dd>{bilingual(project.data.data.name_ar, project.data.data.name_en)}</dd>
              <dt>{t('table.status')}</dt>
              <dd>
                <Badge tone={statusTone(project.data.data.status)}>{project.data.data.status}</Badge>
              </dd>
              <dt>{t('admin.expos.version')}</dt>
              <dd className="numeral">{project.data.data.version}</dd>
            </dl>
            <div className="row">
              {['submit', 'publish'].map((verb) => (
                <button
                  key={verb}
                  type="button"
                  className="btn btn--secondary"
                  disabled={projectAction.isPending}
                  onClick={() =>
                    projectAction.mutate(newIntent({
                      path: `/v1/developer/projects/${projectId}:${verb}`,
                      body: {},
                      ifMatch: project.data?.version ?? project.data?.data.version ?? 1,
                    }))
                  }
                >
                  {verb}
                </button>
              ))}
            </div>
            {projectAction.error ? <ErrorState error={projectAction.error} /> : null}
          </>
        ) : null}
      </Card>

      <Card title={t('dev.unitTypes.title')}>
        <Field label="code" name="unit-type-code" value={unitTypeCode} onChange={setUnitTypeCode} ltr />
        {createUnitType.error ? <ErrorState error={createUnitType.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={createUnitType.isPending || projectId === '' || unitTypeCode === ''}
          onClick={() =>
            createUnitType.mutate(newIntent({
              path: `/v1/developer/projects/${projectId}/unit-types`,
              body: { code: unitTypeCode, name_ar: unitTypeCode, name_en: unitTypeCode },
            }))
          }
        >
          {t('action.create')}
        </button>
      </Card>

      <Card title={t('dev.units.import')} description={t('dev.units.import.desc')}>
        <TextArea
          label="units (JSON array)"
          name="import-rows"
          value={importRows}
          onChange={setImportRows}
          hint='[{"unit_code":"A-101","unit_type_code":"T1"}]'
        />
        {importUnits.error ? <ErrorState error={importUnits.error} /> : null}
        {importUnits.data?.rejected?.length ? (
          <DegradedState title="per-row rejection report">
            <ul>
              {importUnits.data.rejected.map((r) => (
                <li key={r.row} className="ltr">
                  row {r.row}: {r.rule}
                </li>
              ))}
            </ul>
          </DegradedState>
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={importUnits.isPending || projectId === ''}
          onClick={() => {
            let parsed: unknown = [];
            try {
              parsed = JSON.parse(importRows || '[]');
            } catch {
              parsed = [];
            }
            importUnits.mutate(newIntent({
              path: `/v1/developer/projects/${projectId}/units:import`,
              body: { units: parsed },
            }));
          }}
        >
          {t('dev.units.import')}
        </button>
      </Card>

      <Card title={t('dev.price.title')}>
        <AuditStatement>
          <p>
            <Ltr>PUT /v1/developer/units/{'{unitId}'}/price</Ltr> is a compare-and-swap:{' '}
            <Ltr>If-Match</Ltr> is the unit&rsquo;s CURRENT row version and a stale one is a 412,
            never an overwrite. This branch serves no developer unit read, so the version comes from
            the import result or the admin surface and is entered here rather than guessed — it used
            to be hard-coded to <Ltr>1</Ltr>, which is a wrong answer for every unit that has ever
            been touched.
          </p>
        </AuditStatement>
        <Field label="unit_id" name="price-unit" value={unitId} onChange={setUnitId} ltr required />
        <Field
          label="If-Match (unit row version)"
          name="price-version"
          type="number"
          value={priceVersion}
          onChange={setPriceVersion}
          ltr
          required
        />
        <Field
          label="list_price_minor"
          name="price-amount"
          type="number"
          value={priceMinor}
          onChange={setPriceMinor}
          ltr
          required
        />
        <Field label="currency" name="price-currency" value={currency} onChange={setCurrency} ltr required />
        <Field
          label="effective_from"
          name="price-from"
          type="datetime-local"
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          ltr
          required
        />
        {setPrice.error ? (
          <RefusalWithRemedy error={setPrice.error} action="unit.price.manage" retryExact={setPrice.retryLast} />
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={
            setPrice.isPending || unitId === '' || priceVersion === '' || currency === '' || effectiveFrom === ''
          }
          onClick={() =>
            setPrice.mutate(newIntent({
              path: `/v1/developer/units/${unitId}/price`,
              method: 'PUT',
              label: `price ${unitId}`,
              // `handleSetPrice` decodes `list_price_minor`, `currency`, `effective_from`,
              // `effective_to` and `expo_id`, strictly. `amount_minor` was an unknown field.
              body: {
                list_price_minor: Number(priceMinor),
                currency,
                effective_from: new Date(effectiveFrom).toISOString(),
              },
              ifMatch: Number(priceVersion),
            }))
          }
        >
          {t('action.save')}
        </button>
      </Card>

      <Card title={t('dev.offers.title')} description={t('dev.offers.desc')}>
        <AuditStatement>
          <p>
            An offer names the SCOPE it applies to and the object inside it:{' '}
            <Ltr>scope_id</Ltr> is required and must be a uuid, and the validity window is what the
            publication tier is evaluated against.
          </p>
        </AuditStatement>
        {/* THE SCOPE SET IS THE ONE `catalogue.CreateOffer` ACCEPTS (round-2 H6). It is
            `project | unit_type | unit`, checked before the insert so the caller gets a 422 rather
            than a constraint violation. `developer` was never one of them and was the DEFAULT here,
            so the first offer anybody created from this screen was refused — and `scope_id` has no
            meaning under it, because there is no catalogue object an offer could apply to. */}
        <Select
          label="scope"
          name="offer-scope"
          value={offerScope}
          options={['project', 'unit_type', 'unit'].map((x) => ({ value: x, label: x }))}
          onChange={setOfferScope}
        />
        <Field label="scope_id" name="offer-scope-id" value={offerScopeId} onChange={setOfferScopeId} ltr required />
        <Field label="title_ar" name="offer-ar" value={offerTitleAr} onChange={setOfferTitleAr} required />
        <Field label="title_en" name="offer-en" value={offerTitleEn} onChange={setOfferTitleEn} ltr required />
        <Field
          label="valid_from"
          name="offer-from"
          type="datetime-local"
          value={offerFrom}
          onChange={setOfferFrom}
          ltr
          required
        />
        <Field
          label="valid_to"
          name="offer-to"
          type="datetime-local"
          value={offerTo}
          onChange={setOfferTo}
          ltr
          required
        />
        {createOffer.error ? (
          <RefusalWithRemedy error={createOffer.error} action="offer.create" retryExact={createOffer.retryLast} />
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={
            createOffer.isPending || offerScopeId === '' || offerFrom === '' || offerTo === ''
          }
          onClick={() =>
            createOffer.mutate(newIntent({
              path: '/v1/developer/offers',
              label: 'create offer',
              body: {
                scope: offerScope,
                scope_id: offerScopeId,
                kind: 'discount',
                title_ar: offerTitleAr,
                title_en: offerTitleEn,
                valid_from: new Date(offerFrom).toISOString(),
                valid_to: new Date(offerTo).toISOString(),
                publish_tier: 'announced',
              },
            }))
          }
        >
          {t('action.create')}
        </button>
      </Card>
    </>
  );
}

/** `DEV-22` holds and `DEV-29` the hold policy. */
export function DeveloperHolds(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const [orgId, setOrgId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [ttlHours, setTtlHours] = useState('48');
  const [maxExtensions, setMaxExtensions] = useState('2');
  // `catalogue.HoldPolicy.Validate` makes this a REQUIRED field: an empty one is a
  // `policy_version` / `required` field error, so every save from this screen used to be a 422
  // before it reached the domain. It is the developer's own semantic generation for the policy —
  // distinct from `revision_id`, which is the server's and travels in the ETag.
  const [policyVersion, setPolicyVersion] = useState('');
  const create = useApiAction<Hold>([]);
  const action = useApiAction<Hold>([]);
  const policy = useApiAction<HoldPolicy>([]);
  const [hold, setHold] = useState<Hold | null>(null);

  return (
    <>
      <PageTitle title={t('dev.holds.title')}>{t('dev.holds.desc')}</PageTitle>

      <Card title={t('dev.holdPolicy.title')} description={t('dev.holdPolicy.desc')}>
        <AuditStatement>
          <p>
            <Ltr>deal.policy.manage</Ltr> — a change applies ONLY to holds created afterwards: live
            holds keep their snapshotted deadline. The deposit amount is not a field here.
          </p>
        </AuditStatement>
        <Field label="organization_id" name="policy-org" value={orgId} onChange={setOrgId} ltr required />
        <Field label="ttl_hours" name="ttl_hours" type="number" value={ttlHours} onChange={setTtlHours} ltr />
        <Field
          label="max_extensions"
          name="max_extensions"
          type="number"
          value={maxExtensions}
          onChange={setMaxExtensions}
          ltr
        />
        <Field
          label="policy_version"
          name="policy_version"
          value={policyVersion}
          onChange={setPolicyVersion}
          ltr
          required
          hint="Your own generation label for this policy (e.g. 2026.08.1). The server's revision id is separate and comes back in the ETag."
        />
        <p className="muted">
          {/* CREATION AND UPDATE ARE DIFFERENT REQUESTS AND SAY SO. `handleSetHoldPolicy` requires
              `If-None-Match: *` for the first policy and `If-Match: <revision>` for an update, and
              refuses both together or neither. The revision is the SERVER's, returned as the
              `ETag` of the write — never the developer's reusable `policy_version` text. */}
          {policy.lastEtag.current
            ? `If-Match: "${policy.lastEtag.current}"`
            : 'If-None-Match: * — the first policy for this organization'}
        </p>
        {policy.error ? (
          <RefusalWithRemedy error={policy.error} action="deal.policy.manage" retryExact={policy.retryLast} />
        ) : null}
        {policy.data ? (
          <SuccessNote>
            <Ltr>policy_version={policy.data.policy_version}</Ltr>
          </SuccessNote>
        ) : null}
        <button
          className="btn"
          type="button"
          disabled={policy.isPending || orgId === '' || policyVersion.trim() === ''}
          onClick={() =>
            policy.mutate(newIntent({
              path: `/v1/developer/organizations/${orgId}/hold-policy`,
              method: 'PUT',
              label: `hold policy ${orgId}`,
              body: {
                ttl_hours: Number(ttlHours),
                max_extensions: Number(maxExtensions),
                policy_version: policyVersion,
              },
              ...(policy.lastEtag.current
                ? { ifMatch: policy.lastEtag.current }
                : { ifNoneMatch: '*' }),
            }))
          }
        >
          {t('action.save')}
        </button>
      </Card>

      <Card title={t('dev.holds.title')}>
        <Field label="unit_id" name="hold-unit" value={unitId} onChange={setUnitId} ltr />
        {create.error ? <ErrorState error={create.error} /> : null}
        <button
          className="btn"
          type="button"
          disabled={create.isPending || unitId === ''}
          onClick={() => create.mutate(newIntent({ path: '/v1/developer/holds', body: { unit_id: unitId } }), { onSuccess: setHold })}
        >
          {t('action.create')}
        </button>

        {hold ? (
          <>
            <dl className="kv">
              <dt>hold_id</dt>
              <dd>
                <Ltr>{hold.hold_id}</Ltr>
              </dd>
              <dt>expires_at</dt>
              <dd>{formatDateTime(hold.expires_at)}</dd>
              <dt>ttl_hours_snapshot</dt>
              <dd className="numeral">{hold.ttl_hours_snapshot}</dd>
              <dt>extension_count</dt>
              <dd className="numeral">{hold.extension_count}</dd>
            </dl>
            <div className="row">
              {['extend', 'release'].map((verb) => (
                <button
                  key={verb}
                  type="button"
                  className="btn btn--secondary"
                  disabled={action.isPending}
                  onClick={() =>
                    action.mutate(
                      newIntent({ path: `/v1/developer/holds/${hold.hold_id}:${verb}`, body: {}, ifMatch: hold.version }),
                      { onSuccess: setHold },
                    )
                  }
                >
                  {verb}
                </button>
              ))}
            </div>
            {/* `:extend` and `:release` are both FENCED on the hold's version (P4-g, High 10), so
                their refusals need the fenced family rather than a bare error: a 412 means the hold
                in hand is stale, a 409 is a decision.

                AND THE 412 IS OFFERED NO RE-READ, BECAUSE THERE IS NONE (round-3 #17). This used to
                pass `onReread={() => setHold(null)}`, which drew a button labelled "Re-read and
                start again" over a handler that read nothing: the developer surface registers
                `POST /holds`, `:extend` and `:release` and no GET at all, so no route on this branch
                can return a hold's current state. Clearing the panel and calling it a re-read told
                the operator their screen was now in step with the server, which is the one thing it
                could not be. The refusal states what actually happened instead, and the control
                below says exactly what it does. */}
            {action.error ? (
              <>
                <RefusalWithRemedy
                  error={action.error}
                  action="deal.hold.manage"
                  retryExact={action.retryLast}
                  conflictNote={t('dev.holds.conflict')}
                />
                <button type="button" className="btn btn--secondary btn--small" onClick={() => setHold(null)}>
                  {t('dev.holds.forget')}
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </Card>
    </>
  );
}

/** `DEV-16` the duty console and `DEV-17` the queue monitor — live since P1/S8. */
export function DeveloperFloor(): ReactElement {
  const { t, formatDateTime } = useI18n();
  const [participationId, setParticipationId] = useState('');
  const [seatId, setSeatId] = useState('');
  const [incomingRep, setIncomingRep] = useState('');
  const duty = useApiAction<{ duty_session_id?: string; seat_id?: string; status?: string; version?: number }>([]);
  const callNext = useApiAction<QueueEntryRow>([]);
  const queuePath = participationId ? `/v1/developer/participations/${participationId}/queue` : null;
  const queue = useApiList<QueueEntryRow>(queuePath, undefined, { refetchInterval: 5000 });
  const [session, setSession] = useState<{ duty_session_id?: string; status?: string } | null>(null);

  const onDuty = session?.status === 'active' || session?.status === 'on_duty';

  return (
    <>
      <PageTitle title={t('dev.duty.title')}>{t('dev.duty.desc')}</PageTitle>

      <Card title={t('dev.duty.title')}>
        <ParticipationPicker value={participationId} onChange={setParticipationId} />
        <Field label="seat_id" name="seat_id" value={seatId} onChange={setSeatId} ltr />
        {!onDuty ? <EmptyState title={t('dev.duty.offDuty')} /> : null}
        {session ? (
          <dl className="kv">
            <dt>duty_session_id</dt>
            <dd>
              <Ltr>{session.duty_session_id ?? '—'}</Ltr>
            </dd>
            <dt>{t('table.status')}</dt>
            <dd>{session.status ?? '—'}</dd>
          </dl>
        ) : null}
        <div className="row">
          <button
            className="btn"
            type="button"
            disabled={duty.isPending || seatId === '' || participationId === ''}
            onClick={() =>
              duty.mutate(
                newIntent({
                  path: '/v1/developer/duty:check-in',
                  body: { participation_id: participationId, seat_id: seatId, consent_version: 'v1' },
                }),
                { onSuccess: setSession },
              )
            }
          >
            {t('dev.duty.checkIn')}
          </button>
          <button
            className="btn btn--secondary"
            type="button"
            disabled={duty.isPending || !session?.duty_session_id || incomingRep === '' || seatId === ''}
            onClick={() =>
              duty.mutate(
                newIntent({
                  path: '/v1/developer/duty:handover',
                  label: 'duty handover',
                  // `dutyHandover` reads `seat_id` BEFORE it authorises anything, and
                  // `outgoing_duty_session_id` is MANDATORY (Z-H5) — it fences the swap against a
                  // concurrent handover, and the free-seat path is exactly where two handovers race.
                  // Neither was sent, so every handover was a 422 before the domain saw it.
                  body: {
                    seat_id: seatId,
                    outgoing_duty_session_id: session?.duty_session_id ?? '',
                    incoming_rep_user_id: incomingRep,
                    incoming_consent_version: 'v1',
                    reason_code: 'shift_change',
                  },
                }),
                { onSuccess: setSession },
              )
            }
          >
            {t('dev.duty.handover')}
          </button>
          <button
            className="btn btn--destructive"
            type="button"
            disabled={duty.isPending || !session?.duty_session_id}
            onClick={() =>
              duty.mutate(
                newIntent({ path: '/v1/developer/duty:check-out', body: { duty_session_id: session?.duty_session_id ?? '' } }),
                { onSuccess: setSession },
              )
            }
          >
            {t('dev.duty.checkOut')}
          </button>
        </div>
        <Field label="incoming_rep_user_id" name="incoming_rep" value={incomingRep} onChange={setIncomingRep} ltr />
        {/* Check-out with no incoming representative opens an SLA breach interval — stated, not hidden. */}
        <DegradedState title="check-out with no incoming representative">
          {t('dev.duty.desc')}
        </DegradedState>
        {duty.error ? <ErrorState error={duty.error} /> : null}
      </Card>

      <Card title={t('dev.queue.title')} description={t('dev.queue.desc')}>
        {queue.isLoading ? <LoadingState /> : null}
        {queue.error ? <ErrorState error={queue.error} onRetry={() => void queue.refetch()} /> : null}
        {queue.data ? (
          <DataTable<QueueEntryRow>
            caption={t('dev.queue.title')}
            rows={queue.data.items ?? []}
            rowKey={(q) => q.queue_entry_id}
            empty={<EmptyState title={t('dev.queue.empty')} />}
            columns={[
              { key: 'ticket', header: 'ticket_no', cell: (q) => <Ltr>{q.ticket_no}</Ltr> },
              { key: 'position', header: 'position', cell: (q) => <span className="numeral">{q.position}</span> },
              { key: 'eta', header: 'eta_seconds', cell: (q) => <span className="numeral">{q.eta_seconds}</span> },
              {
                key: 'priority',
                header: 'priority_class',
                cell: (q) => <Badge tone={q.priority_ticket ? 'warn' : 'neutral'}>{q.priority_class}</Badge>,
              },
              {
                key: 'status',
                header: t('table.status'),
                cell: (q) => <Badge tone={statusTone(q.status)}>{q.status}</Badge>,
              },
              {
                key: 'expires',
                header: 'call_expires_at',
                cell: (q) => (q.call_expires_at ? formatDateTime(q.call_expires_at) : t('table.none')),
              },
            ]}
          />
        ) : null}
        {!onDuty ? <p className="muted">{t('dev.queue.noSeat')}</p> : null}
        {callNext.error ? <ErrorState error={callNext.error} /> : null}
        {onDuty ? (
          <button
            className="btn"
            type="button"
            disabled={callNext.isPending || participationId === ''}
            onClick={() =>
              callNext.mutate(newIntent({
                path: `/v1/developer/participations/${participationId}/queue:call-next`,
                body: { seat_id: seatId },
              }))
            }
          >
            {t('dev.queue.callNext')}
          </button>
        ) : null}
      </Card>
    </>
  );
}
